
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import {
    FaArrowRight,
    FaCheckCircle,
    FaIdCard,
    FaLock,
    FaShieldAlt,
    FaUserCheck,
    FaGraduationCap,
    FaInfoCircle,
} from "react-icons/fa";
import api from "../api/api";

const BRAND = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    orangeLight: "#FFF4E8",
    text: "#172033",
    muted: "#687386",
    border: "#DCE4EE",
    background: "#F4F7FB",
    white: "#FFFFFF",
    success: "#16803C",
};

const APPLICATION_NUMBER_KEY =
    "paf_entrance_application_number";

const APPLICATION_KEY =
    "paf_entrance_application";

const LOGIN_ENDPOINT =
    "/api/entrance-applications/login/";

const ENTRANCE_APPLICATION_EXAMPLE =
    "PAF-2026-2027-K7M4X9Q2";

const EntranceLogin = () => {
    const navigate = useNavigate();

    const [applicationNumber, setApplicationNumber] =
        useState("");

    const [loading, setLoading] = useState(false);

    const showAlert = ({
        icon,
        title,
        text,
        confirmButtonText = "OK",
        confirmButtonColor = BRAND.navy,
    }) => {
        return Swal.fire({
            icon,
            title,
            text,
            confirmButtonText,
            confirmButtonColor,
            background: BRAND.white,
            color: BRAND.text,
            allowOutsideClick: false,
            allowEscapeKey: true,
            customClass: {
                popup: "paf-login-swal-popup",
                title: "paf-login-swal-title",
                confirmButton:
                    "paf-login-swal-button",
            },
        });
    };

    const handleApplicationNumberChange = (event) => {
        const value = event.target.value
            .toUpperCase()
            .replace(/\s+/g, "");

        setApplicationNumber(value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const value = applicationNumber
            .trim()
            .toUpperCase();

        if (!value) {
            await showAlert({
                icon: "warning",
                title: "Application Number Required",
                text:
                    "Please enter your entrance application number to continue.",
                confirmButtonText:
                    "Enter Application Number",
                confirmButtonColor: BRAND.orange,
            });

            return;
        }

        try {
            setLoading(true);

            const response = await api.post(
                LOGIN_ENDPOINT,
                {
                    application_number: value,
                }
            );

            const application =
                response.data?.application;

            if (!application) {
                throw new Error(
                    "Application information was not returned by the server."
                );
            }

            const returnedApplicationNumber =
                String(
                    application.application_number ||
                        value
                )
                    .trim()
                    .toUpperCase();

            localStorage.setItem(
                APPLICATION_NUMBER_KEY,
                returnedApplicationNumber
            );

            localStorage.setItem(
                APPLICATION_KEY,
                JSON.stringify(application)
            );

            await showAlert({
                icon: "success",
                title: "Application Verified",
                text:
                    "Your application has been successfully verified. You can now continue to your entrance registration.",
                confirmButtonText: "Continue",
                confirmButtonColor: BRAND.navy,
            });

            navigate("/entranceRegistration");
        } catch (error) {
            console.error(
                "Entrance login error:",
                error
            );

            const serverMessage =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                error.response?.data?.errors
                    ?.application_number?.[0] ||
                error.response?.data
                    ?.application_number?.[0];

            await showAlert({
                icon: "error",
                title: "Verification Failed",
                text:
                    serverMessage ||
                    "The application number could not be verified. Please check the number and try again.",
                confirmButtonText: "Try Again",
                confirmButtonColor: BRAND.orange,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="paf-login-page">
            <style>
                {`
                    * {
                        box-sizing: border-box;
                    }

                    .paf-login-page {
                        min-height: 100vh;
                        width: 100%;
                        padding: 30px 20px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: relative;
                        overflow: hidden;
                        background:
                            radial-gradient(
                                circle at 10% 10%,
                                rgba(246,201,68,.12),
                                transparent 28%
                            ),
                            radial-gradient(
                                circle at 90% 90%,
                                rgba(6,58,107,.10),
                                transparent 30%
                            ),
                            ${BRAND.background};
                        font-family:
                            Inter,
                            -apple-system,
                            BlinkMacSystemFont,
                            "Segoe UI",
                            sans-serif;
                    }

                    .paf-background-circle {
                        position: absolute;
                        border-radius: 50%;
                        pointer-events: none;
                        filter: blur(1px);
                    }

                    .paf-circle-one {
                        width: 300px;
                        height: 300px;
                        top: -150px;
                        right: -100px;
                        background:
                            rgba(246,201,68,.10);
                    }

                    .paf-circle-two {
                        width: 260px;
                        height: 260px;
                        bottom: -140px;
                        left: -100px;
                        background:
                            rgba(6,42,75,.08);
                    }

                    .paf-login-wrapper {
                        width: 100%;
                        max-width: 1040px;
                        display: grid;
                        grid-template-columns:
                            minmax(0, .95fr)
                            minmax(390px, .8fr);
                        background: ${BRAND.white};
                        border-radius: 28px;
                        overflow: hidden;
                        position: relative;
                        z-index: 2;
                        box-shadow:
                            0 30px 90px rgba(6,42,75,.14);
                        border:
                            1px solid rgba(6,42,75,.07);
                        animation:
                            pafLoginEntrance .55s ease forwards;
                    }

                    .paf-information-panel {
                        position: relative;
                        padding: 52px;
                        color: white;
                        overflow: hidden;
                        background:
                            linear-gradient(
                                145deg,
                                ${BRAND.navy} 0%,
                                ${BRAND.blue} 72%,
                                #075083 100%
                            );
                    }

                    .paf-information-panel::before {
                        content: "";
                        position: absolute;
                        width: 330px;
                        height: 330px;
                        border-radius: 50%;
                        right: -180px;
                        top: -130px;
                        background:
                            rgba(246,201,68,.09);
                    }

                    .paf-information-panel::after {
                        content: "";
                        position: absolute;
                        width: 220px;
                        height: 220px;
                        border-radius: 50%;
                        left: -130px;
                        bottom: -120px;
                        border:
                            1px solid rgba(255,255,255,.10);
                    }

                    .paf-info-content {
                        position: relative;
                        z-index: 2;
                    }

                    .paf-brand-mark {
                        width: 66px;
                        height: 66px;
                        border-radius: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background:
                            linear-gradient(
                                135deg,
                                ${BRAND.gold},
                                #E8AE16
                            );
                        color: ${BRAND.navy};
                        font-size: 20px;
                        font-weight: 950;
                        letter-spacing: 1px;
                        box-shadow:
                            0 14px 30px rgba(0,0,0,.18);
                        margin-bottom: 28px;
                    }

                    .paf-info-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 7px 12px;
                        border-radius: 999px;
                        background:
                            rgba(255,255,255,.09);
                        border:
                            1px solid rgba(255,255,255,.13);
                        color: ${BRAND.gold};
                        font-size: 11px;
                        font-weight: 850;
                        letter-spacing: .5px;
                        text-transform: uppercase;
                        margin-bottom: 18px;
                    }

                    .paf-info-title {
                        margin: 0;
                        font-size: 38px;
                        line-height: 1.12;
                        font-weight: 950;
                        letter-spacing: -.8px;
                    }

                    .paf-info-title span {
                        color: ${BRAND.gold};
                    }

                    .paf-info-description {
                        max-width: 460px;
                        margin: 18px 0 32px;
                        color:
                            rgba(255,255,255,.76);
                        font-size: 15px;
                        line-height: 1.75;
                    }

                    .paf-feature-list {
                        display: flex;
                        flex-direction: column;
                        gap: 17px;
                    }

                    .paf-feature {
                        display: flex;
                        align-items: flex-start;
                        gap: 13px;
                    }

                    .paf-feature-icon {
                        width: 34px;
                        height: 34px;
                        min-width: 34px;
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background:
                            rgba(246,201,68,.12);
                        color: ${BRAND.gold};
                        font-size: 14px;
                    }

                    .paf-feature-text strong {
                        display: block;
                        color: white;
                        font-size: 13px;
                        font-weight: 850;
                        margin-bottom: 3px;
                    }

                    .paf-feature-text span {
                        display: block;
                        color:
                            rgba(255,255,255,.60);
                        font-size: 12px;
                        line-height: 1.5;
                    }

                    .paf-info-footer {
                        margin-top: 40px;
                        padding-top: 20px;
                        border-top:
                            1px solid rgba(255,255,255,.10);
                        color:
                            rgba(255,255,255,.55);
                        font-size: 11px;
                        line-height: 1.6;
                    }

                    .paf-form-panel {
                        padding: 52px;
                        display: flex;
                        align-items: center;
                        background: white;
                    }

                    .paf-form-content {
                        width: 100%;
                        max-width: 430px;
                        margin: 0 auto;
                    }

                    .paf-mobile-brand {
                        display: none;
                    }

                    .paf-form-icon {
                        width: 58px;
                        height: 58px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 16px;
                        background:
                            ${BRAND.orangeLight};
                        color: ${BRAND.orange};
                        font-size: 21px;
                        margin-bottom: 20px;
                    }

                    .paf-form-heading {
                        margin: 0;
                        color: ${BRAND.navy};
                        font-size: 29px;
                        line-height: 1.2;
                        font-weight: 950;
                        letter-spacing: -.5px;
                    }

                    .paf-form-subtitle {
                        margin: 10px 0 28px;
                        color: ${BRAND.muted};
                        font-size: 14px;
                        line-height: 1.65;
                    }

                    .paf-label {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                        color: ${BRAND.text};
                        font-size: 13px;
                        font-weight: 850;
                        margin-bottom: 9px;
                    }

                    .paf-label-hint {
                        color: ${BRAND.muted};
                        font-size: 11px;
                        font-weight: 600;
                    }

                    .paf-input-wrapper {
                        position: relative;
                    }

                    .paf-input-icon {
                        position: absolute;
                        left: 15px;
                        top: 50%;
                        transform:
                            translateY(-50%);
                        color: ${BRAND.blue};
                        font-size: 17px;
                        pointer-events: none;
                    }

                    .paf-login-input {
                        width: 100%;
                        height: 53px;
                        border:
                            1px solid ${BRAND.border};
                        border-radius: 13px;
                        padding:
                            0 15px 0 45px;
                        background: #FBFCFE;
                        color: ${BRAND.text};
                        font-size: 15px;
                        font-weight: 800;
                        letter-spacing: .7px;
                        transition:
                            border-color .2s ease,
                            box-shadow .2s ease,
                            background .2s ease;
                    }

                    .paf-login-input::placeholder {
                        color: #A4ADBA;
                        font-weight: 600;
                        letter-spacing: .3px;
                    }

                    .paf-login-input:focus {
                        outline: none;
                        border-color:
                            ${BRAND.blue};
                        background: white;
                        box-shadow:
                            0 0 0 4px
                            rgba(6,58,107,.08);
                    }

                    .paf-login-input:disabled {
                        cursor: not-allowed;
                        opacity: .7;
                    }

                    .paf-example-box {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                        margin-top: 11px;
                        padding: 10px 12px;
                        border-radius: 10px;
                        background: #F7F9FC;
                        border:
                            1px solid #E8EDF4;
                    }

                    .paf-example-label {
                        color: ${BRAND.muted};
                        font-size: 10px;
                        font-weight: 750;
                    }

                    .paf-example-number {
                        color: ${BRAND.navy};
                        font-size: 11px;
                        font-weight: 850;
                        letter-spacing: .4px;
                    }

                    .paf-login-button {
                        width: 100%;
                        min-height: 54px;
                        margin-top: 21px;
                        padding: 13px 18px;
                        border: none;
                        border-radius: 13px;
                        background:
                            linear-gradient(
                                135deg,
                                ${BRAND.navy},
                                ${BRAND.blue}
                            );
                        color: white;
                        font-size: 14px;
                        font-weight: 900;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        transition:
                            transform .2s ease,
                            box-shadow .2s ease,
                            opacity .2s ease;
                    }

                    .paf-login-button:not(:disabled):hover {
                        transform: translateY(-2px);
                        box-shadow:
                            0 13px 28px
                            rgba(6,42,75,.22);
                    }

                    .paf-login-button:disabled {
                        cursor: not-allowed;
                        opacity: .58;
                    }

                    .paf-security-box {
                        display: flex;
                        align-items: flex-start;
                        gap: 11px;
                        margin-top: 22px;
                        padding: 14px;
                        border-radius: 12px;
                        background: #F7F9FC;
                        border:
                            1px solid #E8EDF4;
                    }

                    .paf-security-icon-box {
                        width: 31px;
                        height: 31px;
                        min-width: 31px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 9px;
                        background: #EAF5ED;
                        color: ${BRAND.success};
                        font-size: 12px;
                    }

                    .paf-security-text strong {
                        display: block;
                        color: ${BRAND.text};
                        font-size: 11px;
                        font-weight: 850;
                        margin-bottom: 3px;
                    }

                    .paf-security-text span {
                        display: block;
                        color: ${BRAND.muted};
                        font-size: 10px;
                        line-height: 1.5;
                    }

                    .paf-help-text {
                        margin-top: 19px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        gap: 7px;
                        color: ${BRAND.muted};
                        font-size: 11px;
                        text-align: center;
                    }

                    .paf-help-text svg {
                        color: ${BRAND.blue};
                    }

                    .paf-loading-spinner {
                        width: 18px;
                        height: 18px;
                        border:
                            2px solid
                            rgba(255,255,255,.35);
                        border-top-color: white;
                        border-radius: 50%;
                        display: inline-block;
                        animation:
                            pafSpin .7s linear infinite;
                    }

                    @keyframes pafSpin {
                        to {
                            transform: rotate(360deg);
                        }
                    }

                    @keyframes pafLoginEntrance {
                        from {
                            opacity: 0;
                            transform:
                                translateY(24px)
                                scale(.985);
                        }

                        to {
                            opacity: 1;
                            transform:
                                translateY(0)
                                scale(1);
                        }
                    }

                    .paf-login-swal-popup {
                        border-radius: 20px !important;
                        box-shadow:
                            0 25px 70px
                            rgba(6,42,75,.18) !important;
                        width: min(92%, 460px) !important;
                    }

                    .paf-login-swal-title {
                        font-weight: 900 !important;
                        color: ${BRAND.navy} !important;
                    }

                    .paf-login-swal-button {
                        border-radius: 10px !important;
                        font-weight: 800 !important;
                        padding:
                            11px 22px !important;
                    }

                    @media (max-width: 900px) {
                        .paf-login-page {
                            padding: 22px;
                        }

                        .paf-login-wrapper {
                            max-width: 680px;
                            grid-template-columns: 1fr;
                        }

                        .paf-information-panel {
                            display: none;
                        }

                        .paf-form-panel {
                            padding: 45px;
                        }

                        .paf-mobile-brand {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 10px;
                            margin-bottom: 28px;
                        }

                        .paf-mobile-brand-mark {
                            width: 48px;
                            height: 48px;
                            border-radius: 13px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            background:
                                linear-gradient(
                                    135deg,
                                    ${BRAND.navy},
                                    ${BRAND.blue}
                                );
                            color: ${BRAND.gold};
                            font-size: 15px;
                            font-weight: 950;
                        }

                        .paf-mobile-brand-name {
                            color: ${BRAND.navy};
                            font-size: 14px;
                            font-weight: 900;
                        }
                    }

                    @media (max-width: 600px) {
                        .paf-login-page {
                            min-height: 100dvh;
                            padding: 14px;
                            align-items: center;
                        }

                        .paf-login-wrapper {
                            border-radius: 21px;
                        }

                        .paf-form-panel {
                            padding: 29px 21px;
                        }

                        .paf-mobile-brand {
                            justify-content: flex-start;
                            margin-bottom: 25px;
                        }

                        .paf-form-icon {
                            width: 52px;
                            height: 52px;
                            border-radius: 14px;
                            margin-bottom: 16px;
                        }

                        .paf-form-heading {
                            font-size: 25px;
                        }

                        .paf-form-subtitle {
                            font-size: 13px;
                            margin-bottom: 23px;
                        }

                        .paf-label {
                            font-size: 12px;
                        }

                        .paf-label-hint {
                            display: none;
                        }

                        .paf-login-input {
                            height: 52px;
                            font-size: 14px;
                        }

                        .paf-example-box {
                            align-items: flex-start;
                            flex-direction: column;
                            gap: 4px;
                        }

                        .paf-example-number {
                            font-size: 10px;
                        }

                        .paf-login-button {
                            min-height: 53px;
                            font-size: 13px;
                        }

                        .paf-security-box {
                            margin-top: 18px;
                        }

                        .paf-help-text {
                            font-size: 10px;
                            line-height: 1.5;
                        }
                    }

                    @media (max-width: 380px) {
                        .paf-login-page {
                            padding: 9px;
                        }

                        .paf-form-panel {
                            padding:
                                25px 17px;
                        }

                        .paf-form-heading {
                            font-size: 23px;
                        }

                        .paf-login-wrapper {
                            border-radius: 18px;
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .paf-login-wrapper,
                        .paf-login-button {
                            animation: none !important;
                            transition: none !important;
                        }
                    }
                `}
            </style>

            <div className="paf-background-circle paf-circle-one" />
            <div className="paf-background-circle paf-circle-two" />

            <motion.div
                className="paf-login-wrapper"
                initial={{
                    opacity: 0,
                    y: 20,
                }}
                animate={{
                    opacity: 1,
                    y: 0,
                }}
                transition={{
                    duration: 0.55,
                    ease: "easeOut",
                }}
            >
                {/* LEFT INFORMATION PANEL */}
                <section className="paf-information-panel">
                    <div className="paf-info-content">

                        <div className="paf-brand-mark">
                            PAF
                        </div>

                        <div className="paf-info-badge">
                            <FaShieldAlt />
                            Secure Entrance Portal
                        </div>

                        <h1 className="paf-info-title">
                            Welcome to Your
                            <br />
                            <span>
                                Entrance Portal
                            </span>
                        </h1>

                        <p className="paf-info-description">
                            Access your entrance
                            application using your
                            unique application number.
                            Your application information
                            is securely protected throughout
                            the process.
                        </p>

                        <div className="paf-feature-list">

                            <div className="paf-feature">
                                <div className="paf-feature-icon">
                                    <FaUserCheck />
                                </div>

                                <div className="paf-feature-text">
                                    <strong>
                                        Easy Application Access
                                    </strong>

                                    <span>
                                        Use your unique
                                        application number
                                        to securely access
                                        your records.
                                    </span>
                                </div>
                            </div>

                            <div className="paf-feature">
                                <div className="paf-feature-icon">
                                    <FaCheckCircle />
                                </div>

                                <div className="paf-feature-text">
                                    <strong>
                                        Continue Your Registration
                                    </strong>

                                    <span>
                                        Access your entrance
                                        registration and
                                        continue where you
                                        left off.
                                    </span>
                                </div>
                            </div>

                            <div className="paf-feature">
                                <div className="paf-feature-icon">
                                    <FaLock />
                                </div>

                                <div className="paf-feature-text">
                                    <strong>
                                        Secure & Protected
                                    </strong>

                                    <span>
                                        Your application
                                        information is
                                        handled through a
                                        secure portal.
                                    </span>
                                </div>
                            </div>

                        </div>

                        <div className="paf-info-footer">
                            Please keep your application
                            number safe. You will need it
                            whenever you return to your
                            entrance application portal.
                        </div>

                    </div>
                </section>

                {/* LOGIN FORM */}
                <section className="paf-form-panel">
                    <div className="paf-form-content">

                        <div className="paf-mobile-brand">
                            <div className="paf-mobile-brand-mark">
                                PAF
                            </div>

                            <div className="paf-mobile-brand-name">
                                Entrance Portal
                            </div>
                        </div>

                        <motion.div
                            initial={{
                                opacity: 0,
                                y: 10,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                delay: 0.15,
                                duration: 0.45,
                            }}
                        >
                            <div className="paf-form-icon">
                                <FaGraduationCap />
                            </div>

                            <h2 className="paf-form-heading">
                                Entrance Login
                            </h2>

                            <p className="paf-form-subtitle">
                                Enter your unique
                                application number below
                                to continue to your
                                entrance registration.
                            </p>
                        </motion.div>

                        <form onSubmit={handleSubmit}>

                            <label
                                htmlFor="application-number"
                                className="paf-label"
                            >
                                <span>
                                    Application Number
                                </span>

                                <span className="paf-label-hint">
                                    Required
                                </span>
                            </label>

                            <div className="paf-input-wrapper">
                                <FaIdCard className="paf-input-icon" />

                                <input
                                    id="application-number"
                                    className="paf-login-input"
                                    type="text"
                                    value={
                                        applicationNumber
                                    }
                                    onChange={
                                        handleApplicationNumberChange
                                    }
                                    placeholder={
                                        ENTRANCE_APPLICATION_EXAMPLE
                                    }
                                    autoComplete="off"
                                    autoCapitalize="characters"
                                    spellCheck="false"
                                    disabled={loading}
                                    aria-label="Application Number"
                                    aria-required="true"
                                />
                            </div>

                            <div className="paf-example-box">
                                <span className="paf-example-label">
                                    Example Format
                                </span>

                                <span className="paf-example-number">
                                    {ENTRANCE_APPLICATION_EXAMPLE}
                                </span>
                            </div>

                            <button
                                type="submit"
                                className="paf-login-button"
                                disabled={
                                    loading ||
                                    !applicationNumber.trim()
                                }
                            >
                                {loading ? (
                                    <>
                                        <span className="paf-loading-spinner" />

                                        Verifying Application...
                                    </>
                                ) : (
                                    <>
                                        Verify Application

                                        <FaArrowRight />
                                    </>
                                )}
                            </button>

                        </form>

                        <div className="paf-security-box">
                            <div className="paf-security-icon-box">
                                <FaShieldAlt />
                            </div>

                            <div className="paf-security-text">
                                <strong>
                                    Secure Application Access
                                </strong>

                                <span>
                                    Your application information
                                    is protected. Never share
                                    your application number
                                    with unauthorized persons.
                                </span>
                            </div>
                        </div>

                        <div className="paf-help-text">
                            <FaInfoCircle />

                            Make sure your application
                            number is entered correctly.
                        </div>

                    </div>
                </section>
            </motion.div>
        </div>
    );
};

export default EntranceLogin;

